import React, {
  FunctionComponent,
} from 'react';
import { Flex, Box, Text } from 'rebass/styled-components';
import { Input, Select } from '.';
import { ReactComponent as ArrowUp } from '../assets/arrow-up.svg';

interface PaginationProps {
  pageCount: number;
  recordsPerPage?: number;
  onChangeRecordsPerPage: (recordsPerPage: number) => void;
  currentPageNumber?: number;
  onChangePage: (pageNumber: number) => void;
  hasNextPage?: boolean;
  hasPrevPage?: boolean;
}

const customStyles = (themeContext: any) => ({
  option: (provided: any, state: any) => {
    const output = {
      ...provided,
      color: themeContext.mode === 'light' ? themeContext.colors.deepGrey : '#ffffff',
      fontSize: '11px',
      paddingTop: '2px',
      paddingBottom: '2px',
    };

    if (state.isSelected && themeContext.mode === 'dark') {
      output.backgroundColor = 'rgba(255,255,255,0.3)';
    }

    return output;
  },
});

// This appears to be a standard rule for the application. If not, we will
// parameterize it.
const recordsPerPageOptions = [20, 50, 100].map((it: number) => ({
  label: `${it}`,
  value: it,
}));

const Pagination: FunctionComponent<PaginationProps> = ({
  pageCount,
  onChangePage,
  onChangeRecordsPerPage,
  recordsPerPage = 20,
  currentPageNumber = 1,
  hasNextPage = currentPageNumber !== pageCount,
  hasPrevPage = currentPageNumber !== 1,
}: PaginationProps) => {
  const recordsPerPageSelectedOption = recordsPerPageOptions.find(
    ({ value }: any) => value === recordsPerPage,
  );

  return (
    <Flex justifyContent="space-between">
      <Flex alignItems="center">
        <Text variant="primarySmall" mr={1}>Show</Text>
        <Box width={64}>
          <Select
            isSearchable={false}
            options={recordsPerPageOptions}
            value={recordsPerPageSelectedOption}
            controlHeight={24}
            onChange={(opt: any) => {
              onChangeRecordsPerPage(opt?.value);
            }}
            styles={customStyles}
            menuPlacement="auto"
          />
        </Box>
        <Text variant="primarySmall" ml={1}>per page</Text>
      </Flex>
      <Flex variant="primary" alignItems="center">
        <Box
          sx={{ transform: 'rotate(-90deg)', cursor: 'pointer', opacity: hasPrevPage ? '1' : '0.7' }}
          onClick={() => {
            if (hasPrevPage) {
              onChangePage(currentPageNumber - 1);
            }
          }}
          mr={1}
        >
          <ArrowUp />
        </Box>
        <Flex width={32} height={24} ml={1} mr={1}>
          <Input
            value={currentPageNumber}
            handleChange={(ev) => {
              const val = +ev.target.value;
              if (val > pageCount) {
                onChangePage(pageCount);
              } else if (val < 1) {
                onChangePage(1);
              } else {
                onChangePage(val);
              }
            }}
            inputValueFontWeight={700}
            inputValueFontSize={13}
          />
        </Flex>
        <Text variant="primarySmall" mr={1}>
          {' '}
          of
          {' '}
          {pageCount}
        </Text>
        <Box
          sx={{ transform: 'rotate(90deg)', cursor: 'pointer', opacity: hasNextPage ? '1' : '0.7' }}
          onClick={() => {
            if (hasNextPage) {
              onChangePage(currentPageNumber + 1);
            }
          }}
          ml={1}
        >
          <ArrowUp />
        </Box>
      </Flex>
    </Flex>
  );
};

export default Pagination;
